import type { Link } from '@eneco/cms-types';
import { getCombinedLinkFieldValueHref } from '@eneco/common/link';
import { stegaClean } from '@sanity/client/stega';

import { buildLinkTargetsQuery, HOME_PAGE_SLUG, LINK_TARGET_DEPTH } from './queries';
import type { SanityQueryOptions, SanityQueryRunner } from './query-runner';
import type { SanityPage } from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A link, told apart by its shape: the CMS team's `generalLink`, the flow completion and cancelation
 * URLs that share its members, and since schema build 31 a rich text link (ADR-0099), all carry a
 * `linkType`, and nothing else does. In the `development` dataset of 3 October that is 14,318 objects,
 * every one of them a link. A rich text link stored before build 31 (`{ _type: 'link', href }`) has
 * none, so it stays as stored and `mappers.ts#renderSpan` reads its `href` as it is.
 */
export const isSanityLink = (value: unknown): value is Record<string, unknown> =>
  isObject(value) && typeof value.linkType === 'string';

/**
 * A text value with draft mode's edit markers stripped, for building a URL or comparing.
 *
 * In a Studio preview `@sanity/client` hides markers in text values, and its default filter leaves
 * `anchor`, `queryString`, `target` and a site's `domain` encoded, so a URL built from them would
 * break in the preview only. Link text and title keep theirs: that is what makes a link click-to-edit.
 */
const plain = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const cleaned = stegaClean(value).trim();
  return cleaned || undefined;
};

const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

/** What a site contributes to the address of a page on it, from its `site` document. */
export interface LinkSite {
  id: string;
  domain?: string;
  pathPrefix?: string;
  /** Per-language prefixes, such as Over ons in English under `/about-us`. */
  pathPrefixes?: ReadonlyArray<{ language?: string; pathPrefix?: string }>;
}

/** What a linked page, or a linked file, contributes to a URL. */
export interface LinkTarget {
  /** The page's slugs below its site home, root first; empty for the home itself. Absent when its parent chain never reaches a home. */
  segments?: readonly string[];
  language?: string;
  site?: LinkSite;
  /** A file's own address (ADR-0100), for a link to a file. */
  url?: string;
}

/** Everything a link's URL depends on beyond the link itself. */
export interface LinkContext {
  /** The pages and files links point at, by document id. */
  targets: ReadonlyMap<string, LinkTarget>;
  /** The site of the page being shown: a link to a page on it stays root-relative, one to any other site is absolute. */
  site?: LinkSite;
  /** This environment's own origin (`HOST`), such as `https://www.acc.eneco.nl`. */
  host?: string;
  /** The container's default Next.js locale; every other language is the first segment of its pages' paths. */
  defaultLocale?: string;
}

const withoutDraftPrefix = (id: string): string => id.replace(/^drafts\./, '');

/** The id of the page an internal link points at, whether the reference is still a reference or was filled in. */
const targetId = (link: Record<string, unknown>): string | undefined => {
  const { reference } = link;
  if (!isObject(reference)) return undefined;
  if (typeof reference._ref === 'string') return withoutDraftPrefix(reference._ref);
  if (typeof reference._id === 'string') return withoutDraftPrefix(reference._id);
  return undefined;
};

/** The asset a file link points at (ADR-0100), `file-<sha1>-<extension>`, held in Sanity's `file` object. */
const fileId = (link: Record<string, unknown>): string | undefined => {
  const asset = isObject(link.file) ? link.file.asset : undefined;
  return isObject(asset) && typeof asset._ref === 'string' ? asset._ref : undefined;
};

/** The document a link's address comes from: an internal link's page, a file link's file. An external link has none. */
const linkedId = (link: Record<string, unknown>): string | undefined => {
  switch (plain(link.linkType)) {
    case 'internal':
      return targetId(link);
    case 'file':
      return fileId(link);
    default:
      return undefined;
  }
};

/** The ids of the pages and files the links in `value` point at, wherever the links sit. */
const collectTargetIds = (value: unknown, ids: Set<string>): void => {
  if (Array.isArray(value)) {
    for (const item of value) collectTargetIds(item, ids);
    return;
  }
  if (!isObject(value)) return;
  if (isSanityLink(value)) {
    const id = linkedId(value);
    if (id) ids.add(id);
    return;
  }
  for (const member of Object.values(value)) collectTargetIds(member, ids);
};

const toSite = (value: unknown): LinkSite | undefined => {
  if (!isObject(value) || typeof value._id !== 'string') return undefined;
  const pathPrefixes = Array.isArray(value.pathPrefixes)
    ? value.pathPrefixes.filter(isObject).map(entry => ({ language: plain(entry.language), pathPrefix: plain(entry.pathPrefix) }))
    : undefined;
  return {
    id: withoutDraftPrefix(value._id),
    ...(plain(value.domain) ? { domain: plain(value.domain) } : {}),
    ...(plain(value.pathPrefix) ? { pathPrefix: plain(value.pathPrefix) } : {}),
    ...(pathPrefixes ? { pathPrefixes } : {}),
  };
};

/** The site a page's own `site` reference names, when the page was not among the pages asked for. */
const referencedSite = (value: unknown): LinkSite | undefined =>
  isObject(value) && typeof value._ref === 'string' ? { id: withoutDraftPrefix(value._ref) } : undefined;

/** One row of `buildLinkTargetsQuery`. */
interface TargetRow {
  _id: string;
  language?: unknown;
  site?: unknown;
  chain?: unknown;
  beyond?: unknown;
  url?: unknown;
}

const isTargetRow = (value: unknown): value is TargetRow => isObject(value) && typeof value._id === 'string';

/**
 * How many times a chain longer than {@link LINK_TARGET_DEPTH} is followed further. The deepest page
 * in the `development` dataset of 3 October is 7 levels below its home, so one query reaches every
 * home there. The cap only stops a `parentPage` cycle, which the migration can produce, from asking
 * forever.
 */
const MAX_CONTINUATIONS = 3;

/** A chain being walked up to its site home: the slugs met so far, nearest first. */
interface Walking {
  target: LinkTarget;
  nearestFirst: string[];
  /** The ancestor to continue from, where the query's depth ran out. */
  resume?: string;
}

/**
 * Walk one row's chain of slugs, nearest first, into `walking`. Done at the home's slug, which sets the
 * target's segments; dead at a gap or a slug holding a whole path; to be resumed from `beyond` when the
 * query's depth ran out before a home.
 */
const walkChain = (row: TargetRow, walking: Walking): void => {
  walking.resume = undefined;
  const chain = Array.isArray(row.chain) ? row.chain : [];
  for (const slug of chain) {
    const current = plain(slug);
    if (current === HOME_PAGE_SLUG) {
      walking.target.segments = [...walking.nearestFirst].reverse();
      return;
    }
    if (!current || current.includes('/')) return;
    walking.nearestFirst.push(current);
  }
  if (chain.length === LINK_TARGET_DEPTH && typeof row.beyond === 'string') walking.resume = withoutDraftPrefix(row.beyond);
};

const rowsById = (rows: unknown): Map<string, TargetRow> =>
  new Map((Array.isArray(rows) ? rows : []).filter(isTargetRow).map(row => [withoutDraftPrefix(row._id), row]));

/**
 * The pages behind `ids`: each one's slugs up to its site home, its language and its site. A file
 * among them brings its own address, and a file the dataset does not hold brings nothing.
 *
 * One query, written out {@link LINK_TARGET_DEPTH} parents deep (GROQ has no recursive dereference),
 * answers every chain in the dataset. A deeper one is followed from where the query stopped. Asked at
 * the page's own perspective, so a draft page builds its links from draft pages.
 */
export const fetchLinkTargets = async (
  ids: ReadonlySet<string>,
  runner: SanityQueryRunner,
  options: SanityQueryOptions,
  languageField = 'language',
): Promise<Map<string, LinkTarget>> => {
  const targets = new Map<string, LinkTarget>();
  if (ids.size === 0) return targets;
  const query = buildLinkTargetsQuery(languageField);

  let walking: Walking[] = [];
  for (const [id, row] of rowsById(await runner.query<unknown>(query, { ids: [...ids] }, options))) {
    const site = toSite(row.site);
    const language = plain(row.language);
    const url = plain(row.url);
    const target: LinkTarget = { ...(language ? { language } : {}), ...(site ? { site } : {}), ...(url ? { url } : {}) };
    targets.set(id, target);
    const chain: Walking = { target, nearestFirst: [] };
    walkChain(row, chain);
    if (chain.resume) walking.push(chain);
  }

  for (let round = 0; round < MAX_CONTINUATIONS && walking.length > 0; round += 1) {
    const resumeIds = new Set(walking.map(chain => chain.resume as string));
    const rows = rowsById(await runner.query<unknown>(query, { ids: [...resumeIds] }, options));
    for (const chain of walking) {
      const row = rows.get(chain.resume as string);
      if (row) walkChain(row, chain);
      else chain.resume = undefined;
    }
    walking = walking.filter(chain => chain.resume);
  }
  return targets;
};

/** A site's prefix for one language, as `/mkb`, or nothing for a site at the domain's root. */
const sitePrefix = (site: LinkSite | undefined, language: string | undefined): string => {
  const ownPrefix = site?.pathPrefixes?.find(entry => entry.language === language)?.pathPrefix;
  const bare = (ownPrefix ?? site?.pathPrefix ?? '').replace(/^\/+/, '').replace(/\/+$/, '');
  return bare ? `/${bare}` : '';
};

/**
 * The language's own first segment, as Next.js routes it: none for the container's default locale,
 * `/en` for English on the Dutch site. Sitecore writes the same (`/en/about-us/...`), and eneco.be,
 * whose default locale is a placeholder, gets `/nl/` and `/fr/` alike.
 */
const languageSegment = (language: string | undefined, defaultLocale: string | undefined): string => {
  if (!language || !defaultLocale) return '';
  const locale = language.split('-')[0].toLowerCase();
  return locale === defaultLocale.toLowerCase() ? '' : `/${locale}`;
};

/**
 * The origin of a page on another site: this environment's host with the shown site's domain swapped
 * for the target's (`https://www.acc.eneco.nl` to `https://www.acc.woonenergie.nl`), as Sitecore does
 * on production, acceptance and test. A host not on the shown site's domain (a local or hosted PR
 * build) says nothing about the target's, so that falls back to production's `https://www.` form.
 */
const originOf = (domain: string, context: LinkContext): string => {
  const shown = context.site?.domain;
  let host: URL | undefined;
  try {
    host = context.host ? new URL(context.host) : undefined;
  } catch {
    host = undefined;
  }
  if (host && shown && (host.hostname === shown || host.hostname.endsWith(`.${shown}`))) {
    const hostname = `${host.hostname.slice(0, host.hostname.length - shown.length)}${domain}`;
    return `${host.protocol}//${hostname}${host.port ? `:${host.port}` : ''}`;
  }
  return `https://www.${domain}`;
};

/** A page's full address: its language, its site's prefix and its slugs, absolute when it is on another site. */
const addressOf = (target: LinkTarget, context: LinkContext): string | undefined => {
  if (!target.segments) return undefined;
  const path = `${languageSegment(target.language, context.defaultLocale)}${sitePrefix(target.site, target.language)}/${target.segments
    .map(segment => `${segment}/`)
    .join('')}`;
  const otherSite = target.site && context.site && target.site.id !== context.site.id;
  if (!otherSite) return path;
  return target.site?.domain ? `${originOf(target.site.domain, context)}${path}` : undefined;
};

/** Where a link leads before its query string and anchor, as {@link toSharedLink} describes. */
const baseOf = (link: Record<string, unknown>, context: LinkContext | undefined): string | undefined => {
  switch (plain(link.linkType)) {
    case 'internal': {
      if (!context) return isObject(link.reference) ? undefined : plain(link.href);
      const id = targetId(link);
      return id ? addressOf(context.targets.get(id) ?? {}, context) : undefined;
    }
    case 'file': {
      if (!context) return isObject(link.file) ? undefined : plain(link.href);
      const id = fileId(link);
      return id ? context.targets.get(id)?.url : undefined;
    }
    default:
      return plain(link.href);
  }
};

/**
 * A Sanity link in the shape both adapters hand components a link in (`Link`).
 *
 * - An internal link's URL is its page's address, built from `context`, where Sitecore's URL is: the
 *   language and the site's prefix in front, absolute for another site. Without `context` only a link
 *   that holds no reference keeps its `href`: one this function already converted.
 * - A file link's URL is its file's own address (ADR-0100), from `context` in the same way. A file that
 *   was never migrated, or that the dataset does not hold, gives none.
 * - The query string and anchor are added the way `@eneco/next`'s `Link` adds Sitecore's
 *   (`getCombinedLinkFieldValueHref`), and dropped as separate keys.
 * - A link that opens a new tab gets `rel="noopener noreferrer"`.
 * - Text and title are the link's own, or absent: nothing is borrowed from the page it points at.
 *
 * Converting a converted link changes nothing.
 */
export const toSharedLink = (link: Record<string, unknown>, context?: LinkContext): Link => {
  const linkType = plain(link.linkType);
  const base = baseOf(link, context);
  const target = plain(link.target);
  return {
    href: base
      ? getCombinedLinkFieldValueHref({
          href: base,
          querystring: plain(link.queryString) ?? '',
          anchor: plain(link.anchor) ?? '',
        })
      : '',
    ...(hasText(link.text) ? { text: link.text } : {}),
    ...(target ? { target } : {}),
    ...(target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
    ...(linkType ? { linkType } : {}),
    ...(hasText(link.title) ? { title: link.title } : {}),
  };
};

/**
 * `value` with every link in it converted, wherever it sits. Returns new objects.
 *
 * A rich text link keeps its `_key` and its `_type` (`link`; a General Link has no `_type`): its words
 * point at it by that key, and `mappers.ts#renderSpan` finds it by both. Without them the words would
 * lose their link.
 */
const convertLinks = (value: unknown, context: LinkContext): unknown => {
  if (Array.isArray(value)) return value.map(item => convertLinks(item, context));
  if (!isObject(value)) return value;
  if (isSanityLink(value)) {
    const converted = toSharedLink(value, context);
    return value._type === 'link' && typeof value._key === 'string'
      ? { _key: value._key, _type: value._type, ...converted }
      : converted;
  }
  return Object.fromEntries(Object.entries(value).map(([key, member]) => [key, convertLinks(member, context)]));
};

export interface LinkResolution extends SanityQueryOptions {
  host?: string;
  defaultLocale?: string;
}

/**
 * Converts every link on a page, its blocks, its chrome, its shared content and its own fields, into
 * the shape components read (`toSharedLink`), with each internal link's full address and each file
 * link's file. Rich text links are among them, and keep what their words find them by.
 *
 * The pages and files links point at are asked for in one query, together with the page itself, whose
 * site decides which links leave it. A page with no internal or file links asks nothing. Runs per request, after
 * the documents the page's blocks point at are filled in, so a link inside one of them is converted
 * too, and a shared menu gets URLs for the site it is shown on.
 */
export const createLinkResolver =
  (runner: SanityQueryRunner, languageField = 'language') =>
  async (page: SanityPage, { host, defaultLocale, ...options }: LinkResolution): Promise<SanityPage> => {
    const ids = new Set<string>();
    collectTargetIds(page, ids);
    const pageId = typeof page._id === 'string' ? withoutDraftPrefix(page._id) : undefined;
    const targets =
      ids.size > 0
        ? await fetchLinkTargets(pageId ? new Set([...ids, pageId]) : ids, runner, options, languageField)
        : new Map<string, LinkTarget>();
    const site = (pageId ? targets.get(pageId)?.site : undefined) ?? referencedSite(page.site);
    const context: LinkContext = {
      targets,
      ...(site ? { site } : {}),
      ...(host ? { host } : {}),
      ...(defaultLocale ? { defaultLocale } : {}),
    };
    return convertLinks(page, context) as SanityPage;
  };
